/**
 * Per-board search + sort state for the Kanban board header (SE-6488).
 *
 * ## Why the reset happens during render, not in an effect
 *
 * The search query and sort are **per board** — a query typed on one pipeline
 * must never hide cards on the next. Resetting them in an effect is too late:
 * an effect runs AFTER the render that already produced the new board's
 * columns, so on a `cache-first` hit — where there is no loading gap to hide it
 * — board B paints for one frame filtered by board A's query and sorted by
 * board A's field.
 *
 * Resetting during render instead makes React discard and re-run the component
 * before it commits, so that stale combination is never rendered at all. This
 * is React's sanctioned "adjust state when a prop changes" pattern, and it is
 * the render-time counterpart of the key-reset wrapper the repo mandates in
 * place of reset effects (`react-hooks/set-state-in-effect`). A key-reset is
 * not usable here: the state lives in the board screen container, so remounting
 * it would tear down the board's drag state and every sibling hook with it.
 * @module features/player-kanban/hooks/useBoardScopedSearchSort
 */

import { useCallback, useState } from "react";

import {
  DEFAULT_BOARD_SORT,
  nextBoardSort,
  withBoardSortDirection,
  type BoardSortDirection,
  type BoardSortOption,
  type BoardSortState,
} from "../constants/boardSort";

/** Public API returned by {@link useBoardScopedSearchSort}. */
export interface UseBoardScopedSearchSortResult {
  /** Current board-scoped search text. Always `""` for a freshly opened board. */
  readonly boardSearchQuery: string;
  /** Current board-level sort. Always the default for a freshly opened board. */
  readonly boardSort: BoardSortState;
  /** Fired as the user types in the header's search input. */
  readonly onBoardSearchChange: (value: string) => void;
  /** Fired with the newly selected sort field. */
  readonly onBoardSortChange: (option: BoardSortOption) => void;
  /** Fired with the newly selected sort direction. */
  readonly onBoardSortDirectionChange: (direction: BoardSortDirection) => void;
}

/**
 * Own the board's search query and sort, resetting both the moment the open
 * board changes — during render, so the new board never paints through the
 * previous board's filter.
 * @param currentBoardId - The open board. A falsy value leaves state untouched
 *   (a transient `id -> null -> same id` refetch must not wipe the user's input).
 * @returns The {@link UseBoardScopedSearchSortResult}.
 */
export function useBoardScopedSearchSort(
  currentBoardId: string | undefined
): UseBoardScopedSearchSortResult {
  const [boardSearchQuery, setBoardSearchQuery] = useState("");
  const [boardSort, setBoardSort] =
    useState<BoardSortState>(DEFAULT_BOARD_SORT);
  const [scopedBoardId, setScopedBoardId] = useState(currentBoardId);

  const hasSwitchedBoard = Boolean(
    currentBoardId && scopedBoardId !== currentBoardId
  );

  if (hasSwitchedBoard) {
    setScopedBoardId(currentBoardId);
    setBoardSearchQuery("");
    setBoardSort(DEFAULT_BOARD_SORT);
  }

  const onBoardSearchChange = useCallback(
    (value: string) => setBoardSearchQuery(value),
    []
  );

  const onBoardSortChange = useCallback(
    (option: BoardSortOption) =>
      setBoardSort(previous => nextBoardSort(previous, option)),
    []
  );

  const onBoardSortDirectionChange = useCallback(
    (direction: BoardSortDirection) =>
      setBoardSort(previous => withBoardSortDirection(previous, direction)),
    []
  );

  // While a switch is pending React will re-run this component with the reset
  // values, but THIS pass must not hand the caller the previous board's state —
  // it would be read by the memo that derives the rendered columns.
  return {
    boardSearchQuery: hasSwitchedBoard ? "" : boardSearchQuery,
    boardSort: hasSwitchedBoard ? DEFAULT_BOARD_SORT : boardSort,
    onBoardSearchChange,
    onBoardSortChange,
    onBoardSortDirectionChange,
  };
}
